import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiCreditCard,
    FiCheckCircle,
    FiXCircle,
    FiPlus,
    FiList,
} from "react-icons/fi";

import axiosInstance from "../utils/api";
import Navbar from "../components/Navbar";

const Dashboard = () => {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        try {
            const userId = localStorage.getItem("userId");

            const userRes = await axiosInstance.get(`/auth/user/${userId}`);

            setUser(userRes.data.user);
            const stands = userRes.data.user?.stands;
            const contractor = stands[0]?.onsite.contractor;
            setCompanyName(contractor?.companyName || userRes.data.user.companyName);

            const badgesRes = await axiosInstance.get(
                `/badge/by-chef/${userId}`
            );

            setBadges(badgesRes.data.badges || []);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const totalBadges = badges.length;

    const activeBadges = badges.filter(
        (badge) => badge.status === "active"
    ).length;

    const inactiveBadges = badges.filter(
        (badge) => badge.status !== "active"
    ).length;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f5f3ee] flex items-center justify-center">
                <p className="text-slate-500">Loading...</p>
            </div>
        );
    }

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-[#f5f3ee] p-4 md:p-8">
                <div className="max-w-7xl mx-auto">

                    <div className="mb-8">
                        <h1 className="text-3xl text-blue-900 font-medium">
                            Bienvenue, {user?.companyName || user?.name}
                        </h1>

                        <p className="text-slate-500 mt-1">
                            Gérez vos badges et votre équipe
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">

                        <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
                            <div>
                                <p className="text-slate-500 text-sm mb-2">
                                    Badges totaux
                                </p>

                                <h2 className="text-5xl text-blue-900 font-medium">
                                    {totalBadges}
                                </h2>
                            </div>

                            <FiCreditCard
                                size={40}
                                className="text-blue-200"
                            />
                        </div>

                        <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
                            <div>
                                <p className="text-slate-500 text-sm mb-2">
                                    Badges actifs
                                </p>

                                <h2 className="text-5xl text-blue-900 font-medium">
                                    {activeBadges}
                                </h2>
                            </div>

                            <FiCheckCircle
                                size={40}
                                className="text-blue-200"
                            />
                        </div>

                        <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
                            <div>
                                <p className="text-slate-500 text-sm mb-2">
                                    Badges inactifs
                                </p>

                                <h2 className="text-5xl text-slate-500 font-medium">
                                    {inactiveBadges}
                                </h2>
                            </div>

                            <FiXCircle
                                size={40}
                                className="text-slate-200"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                        <div className="bg-white border border-slate-200 rounded-2xl p-6">
                            <h2 className="text-2xl text-blue-900 mb-6">
                                Informations du prestataire
                            </h2>

                            <div className="space-y-5">

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-4 gap-2">
                                    <p className="font-medium text-slate-700">
                                        Société principale
                                    </p>

                                    <p className="text-slate-600">
                                        {companyName || "-"}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-4 gap-2">
                                    <p className="font-medium text-slate-700">
                                        Représentant
                                    </p>

                                    <p className="text-slate-600">
                                        {user?.name || "-"}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-4 gap-2">
                                    <p className="font-medium text-slate-700">
                                        Contact
                                    </p>

                                    <p className="text-slate-600">
                                        {user?.phone || "-"}
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <p className="font-medium text-slate-700">
                                        Email
                                    </p>

                                    <p className="text-slate-600 break-all">
                                        {user?.email || "-"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-2xl p-6">
                            <h2 className="text-2xl text-blue-900 mb-6">
                                Actions rapides
                            </h2>

                            <div className="space-y-4">

                                <button
                                    onClick={() => navigate("/badges/create")}
                                    className="w-full h-14 rounded-xl bg-blue-900 hover:bg-blue-800 transition text-white flex items-center justify-center gap-3 text-lg"
                                >
                                    <FiPlus size={22} />
                                    Créer un nouveau badge
                                </button>

                                <button
                                    onClick={() => navigate("/badges")}
                                    className="w-full h-14 rounded-xl border border-blue-900 text-blue-900 hover:bg-blue-50 transition flex items-center justify-center gap-3 text-lg"
                                >
                                    <FiList size={22} />
                                    Voir tous mes badges
                                </button>
                            </div>

                            <div className="mt-8 pt-6 border-t border-slate-200">
                                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                                    <h3 className="text-blue-900 font-medium mb-3">
                                        Règles importantes
                                    </h3>

                                    <ul className="space-y-2 text-sm text-slate-600 list-disc pl-5">
                                        <li>
                                            Les badges sont liés à votre société.
                                        </li>

                                        <li>
                                            Les accès sont définis par l’administrateur.
                                        </li>

                                        <li>
                                            Vous pouvez définir une date de validité.
                                        </li>

                                        <li>
                                            Un CIN/Passeport ne peut avoir qu’un seul badge actif.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>

    );
};

export default Dashboard;